import { FC, useState } from 'react';
import { useNavigate, useSearchParams } from '@remix-run/react';
import cs from 'classnames';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { $Locale, $UserAgent } from '@lemon8/web-app-shared/atom';
import { TabItemProps, Tabs } from '~/components/tabs';
import LoadMore from '~/components/load-more';
import HashtagCard from '~/components/simple-topic-card';
import { Loading } from '~/components/loading';
import UserLineCard from '~/components/user-line-card';
import { Methods } from '../../constant';
import { $CurrentUserAsAvailable } from '@user/shared/atoms/current-user';
import { $CurrentUserFollowing } from '@user-follow/shared/atoms/current-user-following';
import { $CurrentUserFollower } from '@user-follow/shared/atoms/current-user-follower';
import { $CurrentUserFollowingTopic } from '@user-follow/shared/atoms/current-user-following-topic';
import { useRouteLoader } from '~/utils/use-route-loader';
import { $UserFollowerLoadmore, $UserFollowingLoadmore } from 'shared/atoms';
import { IUserFollowTopic, LegacyUserUtil, User, UserTypeUtil } from 'shared/models';
import { useSEOLinkBuilder } from '~/utils/build-seo-url';

import './style.scss';

interface TabItemData {
  key: 'following' | 'follower';
  value: JSX.Element;
  isLoading: boolean;
  data_list: User[];
  cursor: number;
  has_more: boolean;
  forumList: IUserFollowTopic[];
}

const FollowLoadMore: FC<{ data: TabItemData; className: string }> = ({ data, className }) => {
  const { data_list = [], has_more = false, isLoading, cursor, key, forumList = [] } = data;

  const buildSEOLink = useSEOLinkBuilder();

  const { linkName, userId, userType } = useAtomValue($CurrentUserAsAvailable);
  const { region } = useAtomValue($Locale);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const followerLoader = useRouteLoader(
    Methods.UserFollowerLoadMore,
    {
      user_id: userId,
      count: 10,
      cursor,
    },
    {
      atomFamilies: {
        $UserFollowerLoadmore,
      },
    },
  );

  const followingLoader = useRouteLoader(
    Methods.UserFollowingLoadMore,
    {
      user_id: userId,
      count: 10,
      cursor,
    },
    {
      atomFamilies: {
        $UserFollowingLoadmore,
      },
    },
  );

  const loadData = async (): Promise<{
    data: unknown[];
  }> => {
    if (data.key === 'follower') {
      followerLoader.load();
    } else {
      followingLoader.load();
    }

    return { data: [] };
  };

  let forumName: string;
  if (forumList.length) {
    // 拼接话题名
    const forumNameList: string[] = [];
    forumList.forEach((el: any) => {
      forumNameList.push(el.name);
    });
    forumName = forumNameList.join('、');
  }

  return (
    <div className={cs('follow-feed', className)}>
      {data_list.length ? (
        <LoadMore
          loadData={loadData}
          isEnd={!has_more}
          alwaysShowLoading={true}
          distanceToLoad={500}
          loadingText={<Loading variant="ball" />}
          endText={t('no_more_items')}
          render={() => (
            <div>
              {Boolean(forumList.length) && key === 'following' && (
                <HashtagCard
                  aHref={`/${UserTypeUtil.getLinkNamePrefix(userType)}${linkName}/topic?region=${region}`}
                  isDefault={true}
                  name={t('topic_tag')}
                  className="hashcard-topic-follow"
                  desc={forumName}
                  onClick={(event: any) => {
                    event.preventDefault();
                    navigate(`/${UserTypeUtil.getLinkNamePrefix(userType)}${linkName}/topic?region=${region}`);
                  }}
                />
              )}
              {data_list.map((item, i) => {
                const href = buildSEOLink(LegacyUserUtil.getPath(item));
                return (
                  <UserLineCard
                    userAuthInfo={item.authInfo}
                    key={i}
                    aHref={href}
                    name={item.nickName}
                    desc={item.description}
                    icon_url={item.avatar}
                    onClick={(event: any) => {
                      event.preventDefault();
                      navigate(href);
                    }}
                  />
                );
              })}
            </div>
          )}
        />
      ) : (
        <div className="empty">
          {isLoading || userId === '' ? <Loading variant="ball" /> : <div>{t('no_content')}</div>}
        </div>
      )}
    </div>
  );
};

export const UserFollowTabs: FC = () => {
  const { isMobile } = useAtomValue($UserAgent);
  const isPC = !isMobile;
  const autoFixed = true;
  const { t } = useTranslation();

  const [searchParams] = useSearchParams();
  const [tabKey, setTabKey] = useState(searchParams.get('tabkey'));

  const followingData = useAtomValue($CurrentUserFollowing);
  const followerData = useAtomValue($CurrentUserFollower);
  const followingTopicData = useAtomValue($CurrentUserFollowingTopic);

  const tabs = [
    {
      key: 'following' as const,
      value: <h1>{t('following')}</h1>,
      isLoading: false,
      data_list: tabKey === 'following' ? followingData.data : [],
      cursor: followingData.cursor,
      has_more: followingData.hasMore,
      forumList: followingTopicData.data,
    },
    {
      key: 'follower' as const,
      value: <h1>{t('fans')}</h1>,
      isLoading: false,
      data_list: tabKey === 'follower' ? followerData.data : [],
      cursor: followerData.cursor,
      has_more: followerData.hasMore,
      forumList: [],
    },
  ];

  const onChange = (tab: TabItemProps): void => {
    window.scrollTo(0, 0);
    setTabKey(tab.key);
  };

  return (
    <div className={cs('follow', { isPC })}>
      <Tabs
        className="follow-tab"
        initialPage={tabKey === 'following' ? 0 : 1}
        tabs={tabs}
        onChange={onChange}
        page={tabKey === 'following' ? 0 : 1}
        autoFixed={autoFixed}
      >
        {tabs.map((item, i) => (
          <FollowLoadMore
            className={cs({
              ['fixed']: autoFixed,
            })}
            data={item}
            key={i}
          ></FollowLoadMore>
        ))}
      </Tabs>
    </div>
  );
};
